import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { combineLatest, EMPTY, merge, Subject } from 'rxjs';
import { debounceTime, map, switchMap, tap, finalize, startWith } from 'rxjs/operators';
import { Ladder } from 'src/models/ladder.model';
import { Level } from 'src/models/level.model';
import { Match } from 'src/models/match.model';

export interface AdminUnplayedPairing {
  pairingId: number;
  round: number;
  player1Id: string;
  player1Name: string;
  player2Id: string;
  player2Name: string;
  ladderType: string | null;
}

@Component({
  selector: 'admin-record',
  templateUrl: './record.component.html',
})
export class AdminRecordComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  /** Re-fetch pairings without changing ladder/level (e.g. after saving a match). */
  private readonly refreshPairings$ = new Subject<void>();

  public filterForm: FormGroup;
  public matchForm: FormGroup;
  public ladders: Ladder[] = [];
  public levels: Level[] = [];
  public pairings: AdminUnplayedPairing[] = [];
  public busyLoadPairings = false;
  public busySave = false;
  public message: string | null = null;
  public error: string | null = null;

  public possibleScores: number[] = [...Array(8).keys()];
  public possibleTieScores: number[] = [...Array(21).keys()];
  private validScores: string[][] = [['7', '6'], ['7', '5'], ['6', '4'], ['6', '3'], ['6', '2'], ['6', '1'], ['6', '0']];

  public selectedPairing: AdminUnplayedPairing | null = null;

  /** ngValue from API may be number or string; keep select + FormControl in sync. */
  public compareLevelId = (a: number | string | null, b: number | string | null): boolean => {
    if (a == null || b == null) return a === b;
    return Number(a) === Number(b);
  };

  numericLevelId(lv: Level): number {
    return Number(lv.id);
  }

  constructor(private http: HttpClient, private fb: FormBuilder) {
    this.filterForm = fb.group({
      ladderId: new FormControl(''),
      levelId: new FormControl<number | null>(1),
    });
    this.matchForm = fb.group({
      pairingId: new FormControl<number | null>(null),
      Set1Player1Score: new FormControl('0'),
      Set1Player2Score: new FormControl('0'),
      Set2Player1Score: new FormControl('0'),
      Set2Player2Score: new FormControl('0'),
      Set3Player1Score: new FormControl('0'),
      Set3Player2Score: new FormControl('0'),
      Set1Player1Tie: new FormControl('0'),
      Set1Player2Tie: new FormControl('0'),
      Set2Player1Tie: new FormControl('0'),
      Set2Player2Tie: new FormControl('0'),
      Set3Player1Tie: new FormControl('0'),
      Set3Player2Tie: new FormControl('0'),
    });

    this.matchForm.controls.pairingId.valueChanges.subscribe((id) => {
      const n = id == null ? null : +id;
      this.selectedPairing = this.pairings.find((p) => p.pairingId === n) ?? null;
    });
  }

  ngOnInit() {
    merge(
      combineLatest([
        this.filterForm.controls.ladderId.valueChanges.pipe(
          startWith(this.filterForm.controls.ladderId.value)
        ),
        this.filterForm.controls.levelId.valueChanges.pipe(
          startWith(this.filterForm.controls.levelId.value)
        ),
      ]),
      this.refreshPairings$.pipe(
        map(() => [this.filterForm.controls.ladderId.value, this.filterForm.controls.levelId.value] as const)
      )
    )
      .pipe(
        debounceTime(0),
        switchMap(([ladderId, levelId]) => this.fetchUnplayedPairings(ladderId, levelId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.http.get<Ladder[]>('/ladders').subscribe((resp) => {
      this.ladders.length = 0;
      for (const l of resp) {
        this.ladders.push(new Ladder(l));
      }
      if (this.ladders.length && !this.filterForm.value.ladderId) {
        this.filterForm.patchValue({ ladderId: this.ladders[0].ladderId });
      }
    });
    this.http.get<Level[]>('/levels').subscribe((resp) => {
      this.levels.length = 0;
      for (const l of resp) {
        const id = Number((l as unknown as { id?: number }).id);
        this.levels.push(new Level({ ...l, id: id as unknown as string }));
      }
    });
  }

  /**
   * Always sends ladderId/levelId and matching _ladderId/_levelId so CustomInterceptorAddLadderLevel
   * does not merge stale localStorage values onto this request.
   */
  private fetchUnplayedPairings(ladderId: string | null, levelId: number | string | null) {
    this.message = null;
    this.error = null;
    this.pairings = [];
    this.matchForm.patchValue({ pairingId: null });
    this.selectedPairing = null;

    const lev = levelId == null ? NaN : Number(levelId);
    if (!ladderId || Number.isNaN(lev)) {
      this.busyLoadPairings = false;
      return EMPTY;
    }

    this.busyLoadPairings = true;
    const params = new HttpParams()
      .set('ladderId', ladderId)
      .set('levelId', String(lev))
      .set('_ladderId', ladderId)
      .set('_levelId', String(lev));

    return this.http.get<AdminUnplayedPairing[]>('/admin/unplayedpairings', { params }).pipe(
      tap({
        next: (rows) => {
          this.pairings = rows || [];
          if (this.pairings.length > 0) {
            this.matchForm.patchValue({ pairingId: this.pairings[0].pairingId });
          }
        },
        error: (err) => {
          this.error = err.error?.error || err.error || err.message || 'Failed to load pairings';
        },
      }),
      finalize(() => {
        this.busyLoadPairings = false;
      })
    );
  }

  /** Called after recording a match or from parent code. */
  loadPairings() {
    this.refreshPairings$.next();
  }

  get isTiebreakLadder(): boolean {
    return (this.selectedPairing?.ladderType || '') === 'Tiebreak';
  }

  canSave(): boolean {
    const v = this.matchForm.value;
    if (v.pairingId == null) return false;
    if (this.isTiebreakLadder) {
      const t1 = +v.Set1Player1Tie;
      const t2 = +v.Set1Player2Tie;
      return (t1 === 10 || t2 === 10) && t1 !== t2;
    }
    if (!this.validSetScore([v.Set1Player1Score, v.Set1Player2Score])) return false;
    if (!this.validSetScore([v.Set2Player1Score, v.Set2Player2Score])) return false;
    if (+v.Set3Player1Score > 0 || +v.Set3Player2Score > 0) {
      if (
        !this.validSetScore([v.Set3Player1Score, v.Set3Player2Score]) &&
        !this.valid3rdSetScore([v.Set3Player1Score, v.Set3Player2Score])
      )
        return false;
    }
    return true;
  }

  private validSetScore(score: string[]): boolean {
    const a = +score[0];
    const b = +score[1];
    const hi = a >= b ? [a, b] : [b, a];
    if (this.validScores.some((x) => x.join() === hi.join())) return true;
    return false;
  }

  private valid3rdSetScore(score: string[]): boolean {
    return score.join() === '0,0';
  }

  saveMatch(evt: Event) {
    evt.preventDefault();
    if (!this.canSave() || this.busySave) return;
    this.postMatch(new Match(this.matchForm.value));
  }

  saveForfeit(evt: Event) {
    evt.preventDefault();
    if (this.busySave) return;
    const pid = this.matchForm.value.pairingId;
    if (pid == null) {
      alert('Select a match first.');
      return;
    }
    if (!confirm('Record forfeit with Player 1 as winner (pairing order)?')) return;
    this.postMatch(new Match({ pairingId: pid, Forfeit: true } as Partial<Match>));
  }

  saveRetired(evt: Event) {
    evt.preventDefault();
    if (this.busySave) return;
    const pid = this.matchForm.value.pairingId;
    if (pid == null) {
      alert('Select a match first.');
      return;
    }
    if (!confirm('Record retired with Player 1 as winner (pairing order)?')) return;
    this.postMatch(new Match({ ...this.matchForm.value, Retired: true } as Partial<Match>));
  }

  private postMatch(body: Match) {
    this.busySave = true;
    this.message = null;
    this.error = null;
    const payload = { ...body } as Record<string, unknown>;
    delete payload['opponentId'];

    const ladderId = this.filterForm.controls.ladderId.value;
    const levelId = this.filterForm.controls.levelId.value;
    const lev = levelId == null ? '' : String(Number(levelId));
    let params = new HttpParams();
    if (ladderId && lev && !Number.isNaN(Number(lev))) {
      params = params.set('_ladderId', ladderId).set('_levelId', lev);
    }

    this.http.post<{ message?: string }>('/admin/recordmatch', payload, { params }).subscribe(
      () => {
        this.busySave = false;
        this.message = 'Match recorded.';
        this.loadPairings();
      },
      (err) => {
        this.busySave = false;
        this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message || 'Save failed';
      }
    );
  }
}
